(() => {
  'use strict';

  const doc = document;
  const body = doc.body;
  const rootPath = () => window.Shopify?.routes?.root || '/';
  const $ = (selector, context = doc) => context.querySelector(selector);
  const $$ = (selector, context = doc) => Array.from(context.querySelectorAll(selector));

  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  let activeLayer = null;
  let previousFocus = null;
  let megaCloseTimer = null;

  function storageGet(key) {
    try { return sessionStorage.getItem(key); } catch (_) { return null; }
  }

  function storageSet(key, value) {
    try { sessionStorage.setItem(key, value); } catch (_) {}
  }

  function syncBodyLock() {
    const menuOpen = $('[data-mobile-menu]')?.classList.contains('is-open');
    const layerOpen = $$('.utility-layer.is-open, .subscribe-modal-layer.is-open, .cart-layer.is-open').length > 0;
    body.classList.toggle('is-menu-open', Boolean(menuOpen));
    body.classList.toggle('has-open-layer', Boolean(layerOpen));
  }

  function setMobileMenu(open) {
    const menu = $('[data-mobile-menu]');
    const toggle = $('[data-menu-toggle]');
    if (!menu || !toggle) return;

    menu.classList.toggle('is-open', open);
    toggle.classList.toggle('is-open', open);
    toggle.setAttribute('aria-expanded', String(open));
    $$('.mobile-menu-backdrop').forEach((item) => item.classList.toggle('is-open', open));

    if (!open) {
      $$('[data-mobile-submenu]').forEach((panel) => panel.classList.remove('is-open'));
      $$('[data-submenu-trigger]').forEach((trigger) => {
        trigger.setAttribute('aria-expanded', 'false');
        trigger.querySelector('b')?.classList.remove('is-open');
      });
    }
    syncBodyLock();
  }

  function setSubmenu(trigger) {
    if (window.matchMedia('(min-width: 900px)').matches) return;
    const key = trigger.dataset.submenuTrigger;
    const panel = key ? $(`[data-mobile-submenu="${CSS.escape(key)}"]`) : null;
    if (!panel) return;

    const open = !panel.classList.contains('is-open');
    $$('[data-mobile-submenu]').forEach((item) => item.classList.remove('is-open'));
    $$('[data-submenu-trigger]').forEach((item) => {
      item.setAttribute('aria-expanded', 'false');
      item.querySelector('b')?.classList.remove('is-open');
    });

    panel.classList.toggle('is-open', open);
    trigger.setAttribute('aria-expanded', String(open));
    trigger.querySelector('b')?.classList.toggle('is-open', open);
  }

  function openMega() {
    if (!window.matchMedia('(min-width: 900px)').matches) return;
    clearTimeout(megaCloseTimer);
    const mega = $('[data-desktop-mega]');
    const trigger = $('.mega-trigger');
    if (!mega || !trigger) return;
    mega.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');
  }

  function closeMega(delay = 0) {
    clearTimeout(megaCloseTimer);
    megaCloseTimer = window.setTimeout(() => {
      const mega = $('[data-desktop-mega]');
      const trigger = $('.mega-trigger');
      mega?.classList.remove('is-open');
      trigger?.setAttribute('aria-expanded', 'false');
    }, delay);
  }

  function getLayer(trigger) {
    if (trigger.matches('[data-open-search]')) return $('[data-search-layer]');
    if (trigger.matches('[data-open-subscribe]')) return $('[data-subscribe-layer]');
    if (trigger.matches('[data-open-cart]')) return $('[data-cart-layer]');
    return null;
  }

  function setLayer(layer, open, focusSelector = null) {
    if (!layer) return;

    if (open) {
      previousFocus = doc.activeElement instanceof HTMLElement ? doc.activeElement : null;
      $$('.utility-layer.is-open, .subscribe-modal-layer.is-open, .cart-layer.is-open').forEach((item) => {
        if (item !== layer) {
          item.classList.remove('is-open');
          item.setAttribute('aria-hidden', 'true');
        }
      });
      layer.classList.add('is-open');
      layer.setAttribute('aria-hidden', 'false');
      activeLayer = layer;
      window.setTimeout(() => {
        const target = focusSelector ? $(focusSelector, layer) : $(focusableSelector, layer);
        target?.focus({ preventScroll: true });
      }, 60);
    } else {
      layer.classList.remove('is-open');
      layer.setAttribute('aria-hidden', 'true');
      if (activeLayer === layer) activeLayer = null;
      previousFocus?.focus?.({ preventScroll: true });
      previousFocus = null;
    }
    syncBodyLock();
  }

  function closeAllLayers() {
    $$('.utility-layer.is-open, .subscribe-modal-layer.is-open, .cart-layer.is-open').forEach((layer) => {
      layer.classList.remove('is-open');
      layer.setAttribute('aria-hidden', 'true');
    });
    activeLayer = null;
    syncBodyLock();
  }

  function trapFocus(event) {
    if (event.key !== 'Tab' || !activeLayer?.classList.contains('is-open')) return;
    const items = $$(focusableSelector, activeLayer).filter((item) => item.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && doc.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && doc.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  async function refreshCart(openAfter = true) {
    const [cartResponse, sectionResponse] = await Promise.all([
      fetch(`${rootPath()}cart.js`, { headers: { Accept: 'application/json' } }),
      fetch(`${rootPath()}?section_id=wildton-utilities`, { headers: { Accept: 'text/html' } })
    ]);

    if (!cartResponse.ok || !sectionResponse.ok) throw new Error('Unable to refresh cart.');

    const cart = await cartResponse.json();
    const html = await sectionResponse.text();
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const incoming = $('#WildtonCartDrawerRoot', parsed);
    const current = $('#WildtonCartDrawerRoot');

    if (incoming && current) current.replaceChildren(...Array.from(incoming.childNodes).map((node) => node.cloneNode(true)));
    $$('[data-cart-count]').forEach((counter) => { counter.textContent = String(cart.item_count); });
    if (openAfter) setLayer($('[data-cart-layer]'), true);
  }

  async function changeCart(line, quantity) {
    const response = await fetch(`${rootPath()}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: Number(line), quantity: Number(quantity) })
    });
    if (!response.ok) throw new Error('Unable to update cart.');
    await refreshCart(true);
  }

  function showToast(message) {
    const toast = $('[data-toast]');
    if (!toast) return;
    toast.textContent = message;
    toast.hidden = false;
    window.clearTimeout(showToast.timer);
    showToast.timer = window.setTimeout(() => { toast.hidden = true; }, 2800);
  }

  async function addProduct(form) {
    const button = $('[type="submit"]', form);
    const originalText = button?.textContent;
    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }

    try {
      const response = await fetch(`${rootPath()}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      });
      if (!response.ok) {
        const result = await response.json().catch(() => null);
        throw new Error(result?.description || 'Unable to add this item.');
      }
      await refreshCart(true);
    } finally {
      if (button) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        if (originalText) button.textContent = originalText;
      }
    }
  }

  function switchPopupToSuccess(form) {
    const sheet = form.closest('[data-subscribe-sheet]');
    if (!sheet) return;
    sheet.classList.add('is-subscribed');
    const formStep = $('[data-subscribe-form-step]', sheet);
    const successStep = $('[data-subscribe-success]', sheet);
    if (formStep) formStep.hidden = true;
    if (successStep) successStep.hidden = false;
    storageSet('wildtonSubscribed', 'true');
    window.setTimeout(() => $('[data-copy-discount]', successStep || sheet)?.focus(), 80);
  }

  async function submitNewsletter(form) {
    if (!form.reportValidity()) return;

    const button = $('[type="submit"]', form);
    const buttonText = $('[data-button-text]', button || form);
    const status = $('[data-newsletter-status]', form);
    const successInput = $('[data-success-message]', form);
    const defaultText = button?.dataset.submitLabel || buttonText?.textContent || 'Subscribe';
    let success = false;

    status?.classList.remove('is-success', 'is-error');
    if (status) status.textContent = '';
    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }
    if (buttonText) buttonText.textContent = 'Submitting…';

    try {
      const action = form.action.split('#')[0];
      const response = await fetch(action, {
        method: 'POST',
        body: new FormData(form),
        credentials: 'same-origin',
        headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' }
      });
      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const returnedForm = form.id ? parsed.getElementById(form.id) : null;
      const returnedStatus = returnedForm?.querySelector('.form-status, [data-newsletter-status]');
      const finalUrl = new URL(response.url || action, window.location.origin);

      success = response.ok && (
        finalUrl.searchParams.get('contact_posted') === 'true' ||
        returnedStatus?.classList.contains('is-success') ||
        html.includes('contact_posted=true')
      );

      if (!response.ok) throw new Error(`Newsletter request failed: ${response.status}`);

      if (success) {
        form.reset();
        if (form.hasAttribute('data-popup-newsletter')) {
          switchPopupToSuccess(form);
        } else {
          const message = successInput?.value || 'Thanks for subscribing!';
          if (status) {
            status.textContent = message;
            status.classList.add('is-success');
          }
          if (buttonText) buttonText.textContent = 'Thanks!';
        }
      } else {
        const message = returnedStatus?.textContent?.trim() || 'Please check your email address and try again.';
        if (status) {
          status.textContent = message;
          status.classList.add('is-error');
        }
        if (buttonText) buttonText.textContent = defaultText;
      }
    } catch (error) {
      console.error(error);
      if (status) {
        status.textContent = 'Unable to subscribe right now. Please try again.';
        status.classList.add('is-error');
      }
      if (buttonText) buttonText.textContent = defaultText;
    } finally {
      if (button) {
        button.disabled = success;
        button.removeAttribute('aria-busy');
      }
    }
  }

  async function copyDiscount(button) {
    const box = button.closest('.discount-code-box');
    const codeElement = box ? $('[data-discount-code]', box) : null;
    const code = codeElement?.textContent?.trim();
    if (!code) return;

    try {
      await navigator.clipboard.writeText(code);
    } catch (_) {
      const range = doc.createRange();
      range.selectNodeContents(codeElement);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
      doc.execCommand('copy');
      selection.removeAllRanges();
    }

    const defaultText = button.dataset.copyLabel || 'Copy code';
    button.textContent = button.dataset.copiedLabel || 'Copied!';
    button.classList.add('is-copied');
    window.setTimeout(() => {
      button.textContent = defaultText;
      button.classList.remove('is-copied');
    }, 1800);
  }

  doc.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const menuToggle = target.closest('[data-menu-toggle]');
    if (menuToggle) {
      setMobileMenu(!$('[data-mobile-menu]')?.classList.contains('is-open'));
      return;
    }

    if (target.closest('[data-menu-close]')) {
      setMobileMenu(false);
      return;
    }

    const submenuTrigger = target.closest('[data-submenu-trigger]');
    if (submenuTrigger && !window.matchMedia('(min-width: 900px)').matches) {
      event.preventDefault();
      setSubmenu(submenuTrigger);
      return;
    }

    const layerTrigger = target.closest('[data-open-search], [data-open-subscribe], [data-open-cart]');
    if (layerTrigger) {
      event.preventDefault();
      setMobileMenu(false);
      const layer = getLayer(layerTrigger);
      const focusSelector = layerTrigger.matches('[data-open-search]')
        ? '[data-search-input]'
        : layerTrigger.matches('[data-open-subscribe]')
          ? '#subscribe-email'
          : null;
      setLayer(layer, true, focusSelector);
      return;
    }

    const closeTrigger = target.closest('[data-close-search], [data-close-subscribe], [data-close-cart]');
    if (closeTrigger) {
      event.preventDefault();
      setLayer(closeTrigger.closest('.utility-layer, .subscribe-modal-layer, .cart-layer'), false);
      return;
    }

    const cartChange = target.closest('[data-cart-change]');
    if (cartChange) {
      event.preventDefault();
      cartChange.disabled = true;
      changeCart(cartChange.dataset.cartChange, cartChange.dataset.quantity)
        .catch((error) => {
          console.error(error);
          showToast('Unable to update the cart. Please try again.');
        })
        .finally(() => { cartChange.disabled = false; });
      return;
    }

    const copyButton = target.closest('[data-copy-discount]');
    if (copyButton) {
      event.preventDefault();
      copyDiscount(copyButton);
      return;
    }
  });

  doc.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    if (form.matches('.js-product-form')) {
      event.preventDefault();
      addProduct(form).catch((error) => {
        console.error(error);
        showToast(error.message || 'Unable to add this item.');
      });
      return;
    }

    if (form.matches('[data-newsletter-form]')) {
      event.preventDefault();
      submitNewsletter(form);
    }
  });

  doc.addEventListener('pointerover', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (target?.closest('.mega-trigger, [data-desktop-mega]')) openMega();
  });

  doc.addEventListener('pointerout', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const related = event.relatedTarget instanceof Element ? event.relatedTarget : null;
    const area = target?.closest('.mega-trigger, [data-desktop-mega]');
    if (area && !related?.closest('.mega-trigger, [data-desktop-mega]')) closeMega(120);
  });

  doc.addEventListener('focusin', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (target?.closest('.mega-trigger, [data-desktop-mega]')) openMega();
  });

  doc.addEventListener('keydown', (event) => {
    trapFocus(event);
    if (event.key !== 'Escape') return;
    setMobileMenu(false);
    closeMega();
    closeAllLayers();
  });

  window.addEventListener('resize', () => {
    if (window.matchMedia('(min-width: 900px)').matches) setMobileMenu(false);
    else closeMega();
  }, { passive: true });

  doc.addEventListener('shopify:section:load', () => {
    setMobileMenu(false);
    closeMega();
    closeAllLayers();
    if (storageGet('wildtonSubscribed') === 'true') {
      const popupForm = $('[data-popup-newsletter]');
      if (popupForm) switchPopupToSuccess(popupForm);
    }
  });

  doc.addEventListener('shopify:section:select', () => {
    closeMega();
    closeAllLayers();
  });

  if (storageGet('wildtonSubscribed') === 'true') {
    const popupForm = $('[data-popup-newsletter]');
    if (popupForm) switchPopupToSuccess(popupForm);
  }
})();
